import { INVALIDATION_TEMPLATE_MAP } from '../generated/invalidation-template-map';
import { REL_MAP } from '../generated/rel-map';

const SITE_SCOPED_RE = /^\/v1\/sites\/[^/]+/;

let _errorMessageMap: Partial<Record<string, () => string>> = {};

export function configureErrorMessages(map: Partial<Record<string, () => string>>): void {
  _errorMessageMap = map;
}

/**
 * Orval queryOptions mutator.
 * Called as: addMetaToQuery(options, pathParams, { url, operationId, operationName })
 *
 * Uses `operationName` to look up `REL_MAP` and inject `meta.rel`.
 * For site-scoped endpoints, also injects `meta.siteId` from path params.
 */
export const addMetaToQuery = <T extends { meta?: Record<string, unknown> }>(
  options: T,
  pathParams: Record<string, unknown>,
  operation: { url: string; operationId: string; operationName: string }
): T => {
  const rel = REL_MAP[operation.operationName];
  const errorMessage = _errorMessageMap[operation.operationName]?.();
  const siteId =
    SITE_SCOPED_RE.test(operation.url) && 'id' in pathParams
      ? (pathParams.id as string | number)
      : undefined;

  if (!rel && !siteId && !errorMessage) return options;

  return {
    ...options,
    meta: {
      ...(errorMessage && { errorMessage }),
      ...options.meta,
      ...(rel && { rel }),
      ...(siteId && { siteId }),
    },
  };
};

/**
 * Orval mutationOptions mutator.
 * Called as: addMetaToMutation(options, { url }, { operationId, operationName })
 *
 * Injects `meta.rel` and `meta.invalidateTemplates` so the MutationCache can
 * resolve invalidation targets.
 */
export const addMetaToMutation = <T extends { meta?: Record<string, unknown> }>(
  options: T,
  _urlInfo: { url: string },
  operation: { operationId: string; operationName: string }
): T => ({
  ...options,
  meta: {
    ...options.meta,
    rel: REL_MAP[operation.operationName],
    invalidateTemplates: INVALIDATION_TEMPLATE_MAP[operation.operationName] ?? [],
  },
});
